import styles from '@styles/components/TimeInput.module.css';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { ReactComponent as TimeSVG } from '@assets/SVGs/QontactPoint/TimeSVG.svg';

const normalizeTimePart = (value, maxValue, clamp) => {
	const trimmedValue = value.trim();

	if (!trimmedValue) {
		return { value: '', outOfRange: false };
	}

	if (!/^\d{1,2}$/.test(trimmedValue)) {
		return { value: null, outOfRange: false };
	}

	const parsedValue = Number(trimmedValue);

	if (Number.isNaN(parsedValue)) {
		return { value: null, outOfRange: false };
	}

	if (parsedValue > maxValue) {
		return clamp
			? { value: '00', outOfRange: true }
			: { value: String(parsedValue).padStart(2, '0'), outOfRange: true };
	}

	return { value: String(parsedValue).padStart(2, '0'), outOfRange: false };
};

// onValidityChange is optional: existing callers that don't pass it are unaffected.
// Callers that need to warn the user about an incomplete or out-of-range value can pass
// it to get notified right when that happens (called once per commit, with `false` when
// the value is incomplete/out-of-range and `true` otherwise).
//
// clampOutOfRange (default true, preserves the original behaviour for existing callers):
// when an out-of-range number is typed (e.g. "40"), it's silently rewritten to "00". Pass
// `false` to keep the raw typed digits on screen instead (paired with onValidityChange this
// lets a caller show an inline warning without silently mutating what the user typed).
//
// innerRef (optional, a plain ref object) can be attached to imperatively `focus()` the
// first box, or to call `commitAndGetValue()` to force-read the value exactly as currently
// displayed (bypassing any pending blur/state timing), which is useful right before a
// submit action. Not `forwardRef` on purpose: TS can't infer this plain-JS component's prop
// types through `forwardRef`, so a regular prop is used instead.
const TimeInput = ({
	initValue,
	setHour,
	style = {},
	isEnabled = true,
	onValidityChange = (_isValid) => {},
	clampOutOfRange = true,
	innerRef = null,
	onEnterKey = (_committedValue) => {},
}) => {
	const [hours, setHours] = useState(initValue ? initValue.split(':')[0] : '');
	const [minutes, setMinutes] = useState(initValue ? initValue.split(':')[1] : '');
	const containerRef = useRef(null);
	const hoursInputRef = useRef(null);

	useEffect(() => {
		setHours(initValue ? initValue.split(':')[0] : '');
		setMinutes(initValue ? initValue.split(':')[1] : '');
	}, [initValue]);

	const commitValue = (rawHours, rawMinutes) => {
		const normalizedHours = normalizeTimePart(rawHours, 23, clampOutOfRange);
		const normalizedMinutes = normalizeTimePart(rawMinutes, 59, clampOutOfRange);
		const bothEmpty = normalizedHours.value === '' && normalizedMinutes.value === '';
		const bothComplete = Boolean(normalizedHours.value) && Boolean(normalizedMinutes.value);
		const wasOutOfRange = normalizedHours.outOfRange || normalizedMinutes.outOfRange;

		if (bothEmpty) {
			setHours('');
			setMinutes('');
			setHour('');
			onValidityChange(true);
			return '';
		}

		if (!bothComplete) {
			setHours(normalizedHours.value ?? '');
			setMinutes(normalizedMinutes.value ?? '');
			setHour('');
			onValidityChange(false);
			return '';
		}

		const combinedValue = `${normalizedHours.value}:${normalizedMinutes.value}`;
		setHours(normalizedHours.value);
		setMinutes(normalizedMinutes.value);
		setHour(combinedValue);
		onValidityChange(!wasOutOfRange);
		return combinedValue;
	};

	useImperativeHandle(innerRef, () => ({
		focus: () => hoursInputRef.current?.focus(),
		commitAndGetValue: () => {
			const normalizedHours = normalizeTimePart(hours, 23, clampOutOfRange);
			const normalizedMinutes = normalizeTimePart(minutes, 59, clampOutOfRange);
			commitValue(hours, minutes);
			if (!normalizedHours.value || !normalizedMinutes.value) {
				return '';
			}
			return `${normalizedHours.value}:${normalizedMinutes.value}`;
		},
	}));

	const handleHoursChange = (event) => {
		setHours(event.target.value);
	};

	const handleMinutesChange = (event) => {
		setMinutes(event.target.value);
	};

	const handleKeyDown = (event) => {
		if (event.key === 'Enter') {
			const committedValue = commitValue(hours, minutes);
			event.currentTarget.blur();
			onEnterKey(committedValue);
		}
	};

	const handleBlur = (event) => {
		if (containerRef.current?.contains(event.relatedTarget)) {
			return;
		}

		commitValue(hours, minutes);
	};

	return (
		<div
			ref={containerRef}
			className={styles['hour-input-container']}
			style={style}
			onBlur={handleBlur}>
			<div className={styles['svg-container']}>
				<TimeSVG />
			</div>
			<div
				style={{
					display: 'flex',
					flex: 1,
					justifyContent: 'flex-start',
					alignItems: 'center',
				}}>
				<div className={styles['input-wrapper']}>
					<input
						ref={hoursInputRef}
						type="text"
						value={hours}
						onChange={handleHoursChange}
						onKeyDown={handleKeyDown}
						className={styles['time-input']}
						placeholder="00"
						maxLength="2"
						inputMode="numeric"
						disabled={!isEnabled}
					/>
					<span className={styles['colon']}>:</span>
					<input
						type="text"
						value={minutes}
						onChange={handleMinutesChange}
						onKeyDown={handleKeyDown}
						className={styles['time-input']}
						placeholder="00"
						maxLength="2"
						inputMode="numeric"
						disabled={!isEnabled}
					/>
				</div>
			</div>
		</div>
	);
};

export default TimeInput;
